import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ArrowLeft, Eye, EyeOff, ShieldCheck } from "@/components/icons";

/**
 * "Crea tu nueva contraseña" — se muestra la primera vez que un usuario
 * migrado desde MongoDB inicia sesión en Supabase. Requiere reset_token
 * emitido por POST /api/auth/supabase/login.
 */
export default function CreatePasswordPage() {
  const { completeFirstLogin } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const state = loc.state || {};
  const reset_token = state.reset_token;
  const email = state.email;
  const full_name = state.full_name;

  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const pwdRef = useRef(null);

  useEffect(() => {
    if (!reset_token) {
      nav("/login", { replace: true });
    } else {
      pwdRef.current?.focus();
    }
  }, [reset_token, nav]);

  const strength = useMemo(() => {
    const p = pwd;
    let s = 0;
    if (p.length >= 6) s += 1;
    if (p.length >= 10) s += 1;
    if (/[A-Z]/.test(p) && /[a-z]/.test(p)) s += 1;
    if (/\d/.test(p)) s += 1;
    if (/[^A-Za-z0-9]/.test(p)) s += 1;
    return Math.min(s, 4);
  }, [pwd]);
  const strengthLabel = ["Muy débil", "Débil", "Aceptable", "Buena", "Excelente"][
    strength
  ];

  const submit = async (e) => {
    e?.preventDefault();
    setErr("");
    if (pwd.length < 6) {
      setErr("La contraseña debe tener al menos 6 caracteres.");
      return;
    }
    if (pwd !== pwd2) {
      setErr("Las contraseñas no coinciden.");
      return;
    }
    setLoading(true);
    try {
      const profile = await completeFirstLogin(reset_token, pwd);
      toast.success("¡Listo! Tu nueva contraseña ha quedado activa.");
      const redir = {
        admin: "/admin",
        doctor: "/doctor",
        secretary: "/secretaria",
        pharmacy: "/farmacia",
      }[profile.role] || "/";
      nav(redir, { replace: true });
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-white flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-[400px]">
        <button
          type="button"
          onClick={() => nav("/login")}
          className="mb-6 inline-flex items-center gap-1.5 text-[12px] font-semibold text-black/60 hover:text-black transition"
          data-testid="create-pwd-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" />
          Volver al login
        </button>

        <div className="flex justify-center mb-6">
          <div className="w-14 h-14 rounded-xl bg-[#F2F3F5] border border-black/[0.05] flex items-center justify-center">
            <ShieldCheck
              className="w-6 h-6 text-black"
              strokeWidth={1.75}
              weight="regular"
            />
          </div>
        </div>

        <h1
          data-testid="create-pwd-title"
          className="text-center text-3xl sm:text-[30px] font-semibold text-black tracking-tight leading-tight"
        >
          Crea tu contraseña
        </h1>
        <p className="text-center text-black/50 mt-2 text-sm px-4">
          Por seguridad de la migración a Supabase, {full_name ? full_name.split(" ")[0] : "necesitas"} deb{full_name ? "es" : "es"} crear una nueva
          contraseña.{" "}
          {email && (
            <span className="text-black font-semibold">{email}</span>
          )}
        </p>

        <form onSubmit={submit} className="mt-8 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-black/45 block mb-1.5">
              Nueva contraseña
            </label>
            <div className="relative">
              <input
                ref={pwdRef}
                type={show ? "text" : "password"}
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                autoComplete="new-password"
                required
                minLength={6}
                data-testid="create-pwd-input"
                className="w-full h-11 rounded-lg border border-black/[0.08] bg-white px-3 pr-10 text-[14px] font-semibold text-black focus:outline-none focus:border-black/40 transition"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                tabIndex={-1}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-md hover:bg-black/[0.06] flex items-center justify-center transition"
                data-testid="create-pwd-toggle"
              >
                {show ? (
                  <EyeOff
                    className="w-4 h-4 text-black/50"
                    strokeWidth={1.75}
                    weight="regular"
                  />
                ) : (
                  <Eye
                    className="w-4 h-4 text-black/50"
                    strokeWidth={1.75}
                    weight="regular"
                  />
                )}
              </button>
            </div>
            {pwd && (
              <div className="mt-1.5 flex items-center gap-1.5">
                <div className="flex gap-1 flex-1">
                  {[0, 1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className={`h-1 flex-1 rounded-full ${
                        i < strength ? "bg-black" : "bg-black/10"
                      }`}
                    />
                  ))}
                </div>
                <div className="text-[10px] font-semibold text-black/50">
                  {strengthLabel}
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-black/45 block mb-1.5">
              Confirmar contraseña
            </label>
            <input
              type={show ? "text" : "password"}
              value={pwd2}
              onChange={(e) => setPwd2(e.target.value)}
              autoComplete="new-password"
              required
              minLength={6}
              data-testid="create-pwd-confirm"
              className="w-full h-11 rounded-lg border border-black/[0.08] bg-white px-3 text-[14px] font-semibold text-black focus:outline-none focus:border-black/40 transition"
            />
          </div>

          {err && (
            <div
              className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
              data-testid="create-pwd-error"
            >
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="create-pwd-submit"
            className="w-full h-11 rounded-lg bg-black text-white text-[14px] font-semibold hover:bg-black/85 transition disabled:opacity-50"
          >
            {loading ? "Guardando…" : "Crear contraseña y entrar"}
          </button>
        </form>

        <p className="text-[11px] text-black/40 text-center mt-6 px-4">
          Esta contraseña reemplaza cualquier clave anterior. Guárdala en un
          lugar seguro. No se comparte con nadie.
        </p>
      </div>
    </div>
  );
}
